import { i18n } from "@lingui/core";
import { formatNumber } from "@regrello/core-utils";

/**
 * A select-menu option definition that would control the number of decimal places to show for a
 * numeric-valued field (e.g., Number, Currency).
 */
export type DecimalPlaceOption = {
  value: number;
  label: string;
  preview: string;
};

/**
 * The maximum number of decimal places that can be specified for a numeric-valued field.
 */
export const MAX_DECIMAL_PLACES = 8;

/**
 * Returns an array of select-menu option definitions that would control the number of decimal
 * places to show for a numeric-valued field (e.g., Number, Currency).
 *
 * ___Note:___ This must be invoked at runtime because i18n.locale must first have a chance to be
 * detected and initialized on app load.
 */
export function getDecimalPlaceMenuOptions(): DecimalPlaceOption[] {
  return [0, 1, 2, 3, 4, 5, 6, 7, MAX_DECIMAL_PLACES].map((value) => ({
    value,
    label: value.toString(),

    // This preview shows the number 1 with the specified number of decimal places. Note that we
    // should format this number according to the user's current locale, since various locales use
    // decimal points vs. commas vs. something else.
    preview: formatNumber(1, i18n.locale, { decimalPlaces: value }),
  }));
}

/**
 * Returns the option from the list of decimal place options that matches the given value.
 *
 * @param value - The value to search for.
 * @returns The option from the list of decimal place options that matches the given value, or
 * undefined if no match is found.
 */
export function getDecimalPlaceOption(value: number): DecimalPlaceOption | undefined {
  return getDecimalPlaceMenuOptions().find((option) => option.value === value);
}
